// Integrytas Group - CSM
// useLookupNames — resolve a lookup's code → translated display_text for the
// ACTIVE locale (§8), returned as a plain map. The one place pages should reach
// for when they need to show a FK code as its name (§17.B1/B4): never render the
// raw code, render `names[code] ?? code`.

import { useEffect, useState } from 'react';
import { apiDb } from './api_client';
import { useActiveLocale } from './use_active_locale';

interface Opts {
  /** The code column on the definition table (default `internal_code`). */
  codeCol?: string;
  /** The locale key on the `_tl` table — `locale_code` (default) or, for CMS/EDS, `language_id`. */
  localeCol?: string;
}

type TlRow = Record<string, string>;

/**
 * Imperative form of the hook below — resolve a lookup's code → display_text for
 * `locale` (EN_US fallback, then the raw code). Callers that must resolve a
 * VARIABLE number of lookups (a record-extension manifest, a config-driven
 * screen) cannot call the hook in a loop, so they await this instead.
 */
export async function fetchLookupNames(
  table: string,
  tlTable: string,
  locale: string,
  opts: Opts = {},
): Promise<Record<string, string>> {
  const codeCol = opts.codeCol ?? 'internal_code';
  const localeCol = opts.localeCol ?? 'locale_code';
  const { data } = await apiDb
    .from(table)
    .select(`${codeCol}, ${tlTable} ( ${localeCol}, display_text )`);
  const next: Record<string, string> = {};
  for (const row of (data ?? []) as Record<string, unknown>[]) {
    const code = String(row[codeCol]);
    const tls = (row[tlTable] as TlRow[] | undefined) ?? [];
    const exact = tls.find(t => t[localeCol] === locale);
    const fb = tls.find(t => t[localeCol] === 'EN_US') ?? tls[0];
    next[code] = exact?.display_text ?? fb?.display_text ?? code;
  }
  return next;
}

/** Map of `<codeCol>` → active-locale `display_text` (EN_US fallback). */
export function useLookupNames(table: string, tlTable: string, opts: Opts = {}): Record<string, string> {
  const codeCol = opts.codeCol ?? 'internal_code';
  const localeCol = opts.localeCol ?? 'locale_code';
  const locale = useActiveLocale();
  const [map, setMap] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next = await fetchLookupNames(table, tlTable, locale, { codeCol, localeCol });
      if (!cancelled) setMap(next);
    })();
    return () => { cancelled = true; };
  }, [table, tlTable, codeCol, localeCol, locale]);

  return map;
}
